import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Typography from "@/Utility/Typography";
import Input from "@/Utility/Input/Input";
import Button from "@/Utility/Button/Button";
import ToggleSwitch from "@/Utility/ToggleSwitch";
import Radio from "@/Utility/Radio/Radio";
import { RiDraggable } from "react-icons/ri";
import "@/Utility/Button/Button.jsx";
import Loader from "@/Utility/Loader/Loader";
import {
  createProjectStatus,
  fetchProjectStatuses,
  updateProjectStatus,
} from "@/Redux/Thunks/projectStatusesThunk";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import { selectProjectDetailRaw } from "@/Redux/Slices/projectsManagementSlice";
import { updateProject } from "@/Redux/Thunks/projectManagementThunk";
import { unwrapProjectDetailPayload } from "@/Pages/Projects/projectManagementNormalizers";
import { showSuccess } from "@/utils/toast";
import {
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER,
  getWorkItemStatusCategoryWorkflowColors,
  normalizeWorkItemStatusCategoryKey,
} from "@/constants/workItemStatusCategoryTheme";
import WorkItemStatusCategoryBadge from "@/Utility/WorkItemStatusCategoryBadge";

function resolveThemeGroup(status) {
  const key = normalizeWorkItemStatusCategoryKey(status?.category);
  if (key && DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER.includes(key)) return key;
  const name = String(status?.name ?? "")
    .trim()
    .toLowerCase();
  if (/blocked/.test(name)) return "ON_HOLD";
  if (/estimat/.test(name)) return "ESTIMATIONS";
  if (/review/.test(name)) return "IN_REVIEW";
  if (/on\s*hold|parked|deferred/.test(name)) return "ON_HOLD";
  if (/in\s*progress|doing/.test(name)) return "IN_PROGRESS";
  if (/to\s*do|todo|open|new/.test(name)) return "NEW";
  if (/done|closed|complete/.test(name)) return "CLOSED";
  return "IN_PROGRESS";
}

const WorkflowStatusSettings = () => {
  const dispatch = useDispatch();
  const { projectSlug: projectId } = useParams();
  const statusesResponse = useSelector((state) =>
    projectId ? selectProjectStatusesResponse(state, projectId) : null
  );
  const projectDetailRaw = useSelector(selectProjectDetailRaw);

  // Status list state
  const [statuses, setStatuses] = useState([]);
  const [newStatusName, setNewStatusName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);

  // Transitions state
  const [allowForwardMovement, setAllowForwardMovement] = useState(true);
  const [allowReopenFromDone, setAllowReopenFromDone] = useState(false);
  const [savedForwardMovement, setSavedForwardMovement] = useState(true);
  const [savedReopenFromDone, setSavedReopenFromDone] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  const workflowDirty =
    allowForwardMovement !== savedForwardMovement || allowReopenFromDone !== savedReopenFromDone;

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectStatuses(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    const inner = unwrapProjectDetailPayload(projectDetailRaw);
    if (!inner) return;
    if (inner.workflowForwardOnly !== undefined) {
      const val = Boolean(inner.workflowForwardOnly);
      setAllowForwardMovement(val);
      setSavedForwardMovement(val);
    }
    if (inner.allowReopenFromDone !== undefined) {
      const val = Boolean(inner.allowReopenFromDone);
      setAllowReopenFromDone(val);
      setSavedReopenFromDone(val);
    }
  }, [projectDetailRaw]);

  useEffect(() => {
    const list = Array.isArray(statusesResponse?.data) ? statusesResponse.data : [];
    const sorted = [...list].sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
    setStatuses(sorted);
  }, [statusesResponse]);

  const nextSequenceOrder = useMemo(() => {
    if (!statuses.length) return 1;
    const max = Math.max(
      ...statuses.map((s) => (s?.sequenceOrder != null ? Number(s.sequenceOrder) : 0))
    );
    return Number.isFinite(max) ? max + 1 : statuses.length + 1;
  }, [statuses]);

  // Add new status
  const handleAddStatus = async () => {
    const name = String(newStatusName ?? "").trim();
    if (!name) return;
    if (!projectId) return;
    setSaving(true);
    try {
      await dispatch(
        createProjectStatus({
          projectId,
          body: {
            name,
            category: "IN_PROGRESS",
            sequenceOrder: nextSequenceOrder,
          },
        })
      ).unwrap();
      setNewStatusName("");
      dispatch(fetchProjectStatuses(projectId));
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const handleEditStart = (status) => {
    setEditingId(status.id);
    setEditingName(status.name);
  };

  // Save edit
  const handleEditSave = async () => {
    const name = String(editingName ?? "").trim();
    if (!name) return;
    if (!projectId || !editingId) return;
    const current = statuses.find((s) => String(s.id) === String(editingId));
    if (!current) return;

    setSaving(true);
    try {
      await dispatch(
        updateProjectStatus({
          projectId,
          statusId: editingId,
          body: {
            name,
            sequenceOrder: current.sequenceOrder ?? 1,
          },
        })
      ).unwrap();
      setEditingId(null);
      setEditingName("");
      dispatch(fetchProjectStatuses(projectId));
    } finally {
      setSaving(false);
    }
  };

  // Cancel edit
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName("");
  };

  // Get status color based on category/name (unique per statusId, stable on reorder)
  const getStatusColor = (status) => {
    const group = resolveThemeGroup(status);
    return getWorkItemStatusCategoryWorkflowColors(group);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!projectId) return;
    setSavingWorkflow(true);
    try {
      await dispatch(
        updateProject({
          id: projectId,
          projectData: {
            workflowForwardOnly: allowForwardMovement,
            allowReopenFromDone,
          },
        })
      ).unwrap();
      setSavedForwardMovement(allowForwardMovement);
      setSavedReopenFromDone(allowReopenFromDone);
      showSuccess("Workflow settings saved");
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleDropReorder = async (targetId) => {
    if (!draggingId || !targetId || String(draggingId) === String(targetId)) {
      setDraggingId(null);
      return;
    }
    const fromIndex = statuses.findIndex((s) => String(s.id) === String(draggingId));
    const toIndex = statuses.findIndex((s) => String(s.id) === String(targetId));
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingId(null);
      return;
    }
    const next = [...statuses];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setStatuses(next);
    setDraggingId(null);

    if (!projectId) return;
    setSaving(true);
    try {
      await Promise.all(
        next.map((s, index) =>
          dispatch(
            updateProjectStatus({
              projectId,
              statusId: s.id,
              body: {
                name: s.name,
                sequenceOrder: index + 1,
              },
            })
          ).unwrap()
        )
      );
      dispatch(fetchProjectStatuses(projectId));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-md shadow-sm p-6 space-y-8">
      {/* Header */}
      <div>
        <Typography variant="h4" className="font-semibold text-text-primary mb-1">
          Workflow & Status Configuration
        </Typography>
        <Typography variant="text2" className="text-text-secondary">
          Define how work moves from start to completion.
        </Typography>
      </div>

      {/* Status Configuration Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Typography variant="text2" className="font-semibold text-text-primary whitespace-nowrap">
            Status Configuration
          </Typography>
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Input
              type="text"
              placeholder="Enter status name..."
              value={newStatusName}
              onChange={(e) => setNewStatusName(e.target.value)}
              noWrapper
              className="border border-border-primary rounded-md px-3 py-2 text-sm flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleAddStatus()}
            />
            <Button
              variant="primary"
              size="md"
              onClick={handleAddStatus}
              className="px-4 py-2"
              disabled={saving}
            >
              Add
            </Button>
          </div>
        </div>

        {/* Status List */}
        <div className="space-y-2">
          {saving ? (
            <div className="flex items-center justify-center py-4">
              <Loader />
            </div>
          ) : null}
          {statuses.map((status) => {
            const colors = getStatusColor(status);
            const isEditing = editingId === status.id;

            return (
              <div
                key={status.id}
                className="flex items-center justify-between px-3 py-2.5 rounded-md transition-colors"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.text,
                }}
                draggable={!saving}
                onDragStart={() => setDraggingId(status.id)}
                onDragOver={(e) => {
                  if (saving) return;
                  e.preventDefault();
                }}
                onDrop={() => handleDropReorder(status.id)}
              >
                <div className="flex items-center gap-3">
                  {/* Drag Handle */}
                  <RiDraggable
                    className="cursor-grab opacity-60 hover:opacity-100"
                    style={{ color: colors.text }}
                  />
                  {isEditing ? (
                    <Input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditSave();
                        if (e.key === "Escape") handleEditCancel();
                      }}
                      autoFocus
                      className="px-2 py-1 rounded text-sm bg-white text-text-primary border border-border-primary"
                    />
                  ) : (
                    <Typography
                      variant="span1"
                      className="font-medium"
                      style={{ color: colors.text }}
                    >
                      {status.name}
                    </Typography>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        onClick={handleEditSave}
                        className="text-sm font-medium !bg-transparent border-0 !p-0 !m-0 !px-0 !py-0 "
                        style={{ color: colors.text }}
                      >
                        Save
                      </Button>
                      <Typography
                        variant="span2"
                        className="text-sm font-medium"
                        component="span"
                        style={{ color: colors.text }}
                      >
                        |
                      </Typography>
                      <Button
                        onClick={handleEditCancel}
                        className="text-sm font-medium !bg-transparent border-0 !p-0 !m-0 !px-0 !py-0 "
                        style={{ color: colors.text }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleEditStart(status)}
                        className="text-sm font-medium !bg-transparent border-0 !p-0 !m-0 !px-0 !py-0 "
                        style={{ color: colors.text }}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Categories Section */}
      <div className="space-y-3">
        <Typography variant="text2" className="font-semibold text-text-primary">
          Status Categories
        </Typography>

        <div className="flex flex-wrap gap-2">
          {DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER.map((category) => (
            <WorkItemStatusCategoryBadge key={category} category={category} />
          ))}
        </div>
      </div>

      {/* Transitions Section */}
      <div className="space-y-4">
        <Typography variant="text2" className="font-semibold text-text-primary">
          Transitions
          <Typography
            variant="span2"
            className="font-normal text-text-secondary pl-1"
            component="span"
          >
            (Simple)
          </Typography>
        </Typography>

        <div className="space-y-3">
          {/* Forward-only toggle */}
          <div className="flex items-start gap-3">
            <ToggleSwitch
              checked={allowForwardMovement}
              onChange={setAllowForwardMovement}
              size="sm"
            />
            <div>
              <Typography variant="text2" className="text-text-primary font-medium">
                Restrict to forward-only movement
              </Typography>
              <Typography variant="text2" className="text-text-secondary">
                Users can only move items to a status with a higher order (e.g. To Do → In Progress
                → Done). Backward moves are blocked.
              </Typography>
            </div>
          </div>

          {/* Reopen from Done — child of forward-only */}
          <div
            className={`flex items-start gap-3 pl-10 ${!allowForwardMovement ? "opacity-40 pointer-events-none" : ""}`}
          >
            <ToggleSwitch
              checked={allowReopenFromDone}
              onChange={setAllowReopenFromDone}
              size="sm"
              disabled={!allowForwardMovement}
            />
            <div>
              <Typography variant="text2" className="text-text-primary font-medium">
                Allow reopening from Done
              </Typography>
              <Typography variant="text2" className="text-text-secondary">
                Exception to forward-only: items in Done can still be moved back to an earlier
                status.
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div>
        <Button
          variant="primary"
          size="md"
          onClick={handleSaveChanges}
          className="px-6"
          disabled={savingWorkflow || !workflowDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default WorkflowStatusSettings;
